import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Settings, Upload, Database, CheckCircle, AlertTriangle, BarChart3,
  Plus, Trash2, Download, FileText, Calendar, Zap, Flame, Droplets,
  Factory, Recycle, Cpu, ChevronRight, ChevronDown, X, Save, RefreshCw,
  FileSpreadsheet, Eye, Check, AlertCircle, Info, Loader2,
  Sparkles, TrendingUp, TrendingDown, Minus, PieChart, Activity,
  FileWarning, BookOpen
} from 'lucide-react';
import { useAlert } from '../../context/AlertContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Step configuration
const STEPS = [
  { id: 1, name: 'Project Setup', icon: Settings, description: 'Define baseline period and boundaries' },
  { id: 2, name: 'Document Intake', icon: Upload, description: 'Upload bills and records' },
  { id: 3, name: 'Time-Series Data', icon: Database, description: 'Enter monthly consumption data' },
  { id: 4, name: 'Data Validation', icon: CheckCircle, description: 'Review and validate data' },
  { id: 5, name: 'Gap Resolution', icon: AlertTriangle, description: 'Fill missing data' },
  { id: 6, name: 'Baseline Summary', icon: BarChart3, description: 'View calculations and emissions' }
];

// Data type tabs for Step 3
const DATA_TABS = [
  { id: 'electricity', name: 'Electricity', icon: Zap, color: 'yellow' },
  { id: 'fuels', name: 'Fuels', icon: Flame, color: 'orange' },
  { id: 'water', name: 'Water', icon: Droplets, color: 'blue' },
  { id: 'production', name: 'Production', icon: Factory, color: 'green' },
  { id: 'waste', name: 'Waste', icon: Recycle, color: 'gray' },
  { id: 'equipment', name: 'Equipment', icon: Cpu, color: 'purple' }
];

// Fuel types
const FUEL_TYPES = ['Diesel', 'Furnace Oil', 'LPG', 'PNG', 'Coal', 'Biomass', 'Petrol'];

// Water source types
const WATER_SOURCES = ['Municipal', 'Borewell', 'Tanker', 'Recycled', 'Rainwater'];

// Waste types
const WASTE_TYPES = ['Hazardous', 'Non-Hazardous', 'ETP Sludge', 'E-Waste', 'Plastic', 'Metal Scrap'];

// Control types for equipment
const CONTROL_TYPES = ['On/Off', 'VFD', 'Throttle', 'Modulating', 'Manual'];

const DesktopAssessment = ({ studyId: propStudyId, study: propStudy }) => {
  const { showSuccess, showError, showWarning } = useAlert();
  const [searchParams] = useSearchParams();
  const studyId = propStudyId || searchParams.get('study_id');

  // State
  const [currentStep, setCurrentStep] = useState(1);
  const [study, setStudy] = useState(propStudy || null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Step 1: Project Setup
  const [projectSetup, setProjectSetup] = useState({
    baseline_start: '',
    baseline_end: '',
    baseline_months: 24,
    reporting_boundary: ['scope1', 'scope2'],
    notes: ''
  });
  
  // Step 2: Document Intake
  const [documents, setDocuments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Step 3: Time-Series Data
  const [activeDataTab, setActiveDataTab] = useState('electricity');
  const [electricityData, setElectricityData] = useState([]);
  const [fuelsData, setFuelsData] = useState([]);
  const [waterData, setWaterData] = useState([]);
  const [productionData, setProductionData] = useState([]);
  const [wasteData, setWasteData] = useState([]);
  const [equipmentData, setEquipmentData] = useState([]);
  
  // Step 4: Validation
  const [validationResults, setValidationResults] = useState(null);
  const [validating, setValidating] = useState(false);
  
  // Step 5: Gap Resolution
  const [gaps, setGaps] = useState([]);
  
  // Step 6: Baseline Summary
  const [baselineSummary, setBaselineSummary] = useState(null);
  const [calculatingBaseline, setCalculatingBaseline] = useState(false);
  const [generatingNarrative, setGeneratingNarrative] = useState(false);

  // Fetch baseline summary function - can be called independently
  const fetchBaselineSummary = useCallback(async () => {
    if (!studyId) return;
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const baselineRes = await axios.get(`${API}/api/ers/data/baseline/${studyId}/summary`, config);
      if (baselineRes.data?.calculated) {
        setBaselineSummary(baselineRes.data);
      }
    } catch (err) {
      // Baseline not calculated yet - that's fine
      console.log('Baseline not yet calculated');
    }
  }, [studyId]);

  // Fetch study data on mount
  useEffect(() => {
    if (studyId && !propStudy) {
      fetchStudyData();
    } else if (studyId && propStudy) {
      // If study is passed as prop, still fetch time-series and baseline data
      const fetchData = async () => {
        setLoading(true);
        try {
          const token = localStorage.getItem('token');
          const config = { headers: { Authorization: `Bearer ${token}` } };
          
          // Set study from prop
          setStudy(propStudy);
          
          // Set project setup from prop study
          setProjectSetup({
            baseline_start: propStudy.baseline_start || '',
            baseline_end: propStudy.baseline_end || '',
            baseline_months: propStudy.baseline_months || 24,
            reporting_boundary: propStudy.reporting_boundary || ['scope1', 'scope2'],
            notes: propStudy.desktop_notes || ''
          });
          
          // Fetch documents
          try {
            const docsRes = await axios.get(`${API}/api/ers/documents/${studyId}`, config);
            setDocuments(docsRes.data || []);
          } catch (e) {
            setDocuments([]);
          }
          
          // Fetch time-series data
          await fetchTimeSeriesData(config);
          
          // Fetch baseline summary
          await fetchBaselineSummary();
        } catch (error) {
          console.error('Error fetching data:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [studyId, propStudy, fetchBaselineSummary]);

  // Fetch baseline when navigating to Step 6
  useEffect(() => {
    if (currentStep === 6 && studyId && !baselineSummary) {
      fetchBaselineSummary();
    }
  }, [currentStep, studyId, baselineSummary, fetchBaselineSummary]);

  const fetchStudyData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [studyRes, docsRes] = await Promise.all([
        axios.get(`${API}/api/ers/studies/${studyId}`, config),
        axios.get(`${API}/api/ers/documents/${studyId}`, config).catch(() => ({ data: [] }))
      ]);
      
      setStudy(studyRes.data);
      setDocuments(docsRes.data || []);
      
      // Set project setup from study
      if (studyRes.data) {
        setProjectSetup({
          baseline_start: studyRes.data.baseline_start || '',
          baseline_end: studyRes.data.baseline_end || '',
          baseline_months: studyRes.data.baseline_months || 24,
          reporting_boundary: studyRes.data.reporting_boundary || ['scope1', 'scope2'],
          notes: studyRes.data.desktop_notes || ''
        });
      }
      
      // Fetch time-series data
      await fetchTimeSeriesData(config);
      
      // Fetch baseline summary if exists
      await fetchBaselineSummary();
      
    } catch (error) {
      console.error('Error fetching study data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeSeriesData = async (config) => {
    try {
      const [elecRes, fuelsRes, waterRes, prodRes, wasteRes, equipRes] = await Promise.all([
        axios.get(`${API}/api/ers/data/electricity/${studyId}`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/api/ers/data/fuels/${studyId}`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/api/ers/data/water/${studyId}`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/api/ers/data/production/${studyId}`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/api/ers/data/waste/${studyId}`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/api/ers/data/equipment/${studyId}`, config).catch(() => ({ data: [] }))
      ]);
      
      // Map backend field names to frontend field names
      const mapElectricityData = (data) => data.map(row => ({
        ...row,
        period: row.period || row.month,
        kwh_consumption: row.kwh_consumption || row.consumed_units_kwh,
        max_demand_kva: row.max_demand_kva || row.md_kva,
        comment: row.comment || row.notes
      }));
      
      const mapFuelsData = (data) => data.map(row => ({
        ...row,
        period: row.period || row.month,
        comment: row.comment || row.notes
      }));
      
      const mapWaterData = (data) => data.map(row => ({
        ...row,
        period: row.period || row.month,
        source_type: row.source_type || row.source,
        quantity_kl: row.quantity_kl || row.fresh_m3,
        comment: row.comment || row.notes
      }));
      
      const mapProductionData = (data) => data.map(row => ({
        ...row,
        period: row.period || row.month,
        production_qty: row.production_qty || row.quantity,
        unit: row.unit || row.basis
      }));
      
      const mapWasteData = (data) => data.map(row => ({
        ...row,
        period: row.period || row.month,
        quantity: row.quantity || row.quantity_mt,
        unit: row.unit || 'MT'
      }));
      
      setElectricityData(elecRes.data?.length > 0 ? mapElectricityData(elecRes.data) : [createEmptyElectricityRow()]);
      setFuelsData(fuelsRes.data?.length > 0 ? mapFuelsData(fuelsRes.data) : [createEmptyFuelsRow()]);
      setWaterData(waterRes.data?.length > 0 ? mapWaterData(waterRes.data) : [createEmptyWaterRow()]);
      setProductionData(prodRes.data?.length > 0 ? mapProductionData(prodRes.data) : [createEmptyProductionRow()]);
      setWasteData(wasteRes.data?.length > 0 ? mapWasteData(wasteRes.data) : [createEmptyWasteRow()]);
      setEquipmentData(equipRes.data?.length > 0 ? equipRes.data : [createEmptyEquipmentRow()]);
    } catch (error) {
      console.error('Error fetching time-series data:', error);
    }
  };

  // Empty row creators
  const createEmptyElectricityRow = () => ({
    id: `temp_${Date.now()}`,
    site_id: study?.site_id || 'S001',
    meter_id: '',
    period: '',
    kwh_consumption: '',
    renewable_kwh: '',  // Added for renewable electricity tracking
    max_demand_kva: '',
    power_factor: '',
    bill_amount: '',
    comment: ''
  });

  const createEmptyFuelsRow = () => ({
    id: `temp_${Date.now()}`,
    site_id: study?.site_id || 'S001',
    fuel_type: 'Diesel',
    period: '',
    quantity: '',
    unit: 'L',
    bill_amount: '',
    comment: ''
  });

  const createEmptyWaterRow = () => ({
    id: `temp_${Date.now()}`,
    site_id: study?.site_id || 'S001',
    source_type: 'Municipal',
    period: '',
    quantity_kl: '',
    bill_amount: '',
    comment: ''
  });

  const createEmptyProductionRow = () => ({
    id: `temp_${Date.now()}`,
    site_id: study?.site_id || 'S001',
    product_id: 'TotalOutput',
    period: '',
    production_qty: '',
    unit: 'MT'
  });

  const createEmptyWasteRow = () => ({
    id: `temp_${Date.now()}`,
    site_id: study?.site_id || 'S001',
    waste_type: 'Non-Hazardous',
    period: '',
    quantity: '',
    unit: 'MT',
    disposal_method: ''
  });

  const createEmptyEquipmentRow = () => ({
    id: `temp_${Date.now()}`,
    site_id: study?.site_id || 'S001',
    equipment_id: '',
    equipment_type: '',
    location: '',
    rated_power_kw: '',
    qty: 1,
    control_type: 'On/Off',
    operating_hours: '',
    load_percent: '',
    installed_date: '',
    notes: ''
  });

  // Save handlers
  const saveProjectSetup = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/ers/studies/${studyId}`, {
        ...study,
        baseline_start: projectSetup.baseline_start,
        baseline_end: projectSetup.baseline_end,
        baseline_months: projectSetup.baseline_months,
        reporting_boundary: projectSetup.reporting_boundary,
        desktop_notes: projectSetup.notes
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (error) {
      console.error('Error saving project setup:', error);
    } finally {
      setSaving(false);
    }
  };

  const saveTimeSeriesData = async (dataType, data) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      // Filter out empty rows before saving
      const validData = data.filter(row => {
        if (dataType === 'electricity') {
          return row.period || row.month || row.kwh_consumption;
        }
        return row.period || row.month;
      });
      
      // Use bulk save endpoint
      await axios.post(`${API}/api/ers/data/${dataType}/${studyId}/bulk`, validData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      showSuccess(`${dataType} data saved successfully!`);
    } catch (error) {
      console.error(`Error saving ${dataType} data:`, error);
      showError(`Failed to save ${dataType} data. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  // File upload handler
  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files.length) return;
    
    setUploadingFile(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      formData.append('study_id', studyId);
      
      const response = await axios.post(`${API}/api/ers/documents/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setDocuments(prev => [...prev, ...response.data]);
    } catch (error) {
      console.error('Error uploading files:', error);
    } finally {
      setUploadingFile(false);
    }
  };

  // AI Bill Parsing
  const parseDocumentWithAI = async (documentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/ers/documents/parse/${documentId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Auto-fill data based on parsed results
      if (response.data.parsed_data) {
        const parsed = response.data.parsed_data;
        if (parsed.type === 'electricity') {
          setElectricityData(prev => [...prev, ...parsed.rows]);
        } else if (parsed.type === 'fuel') {
          setFuelsData(prev => [...prev, ...parsed.rows]);
        }
        // ... handle other types
      }
    } catch (error) {
      console.error('Error parsing document:', error);
    }
  };

  // Run validation
  const runValidation = async () => {
    setValidating(true);
    try {
      const token = localStorage.getItem('token');
      // Validation now fetches data from database, no need to pass in body
      const response = await axios.post(`${API}/api/ers/data/validate/${studyId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setValidationResults(response.data);
      setGaps(response.data.gaps || []);
    } catch (error) {
      console.error('Error running validation:', error);
      // Show error instead of mock data
      showError('Failed to run validation. Please try again.');
    } finally {
      setValidating(false);
    }
  };

  // Calculate baseline
  const calculateBaseline = async () => {
    setCalculatingBaseline(true);
    try {
      const token = localStorage.getItem('token');
      
      // Call the new comprehensive baseline calculation endpoint
      const response = await axios.post(`${API}/api/ers/data/baseline/${studyId}/calculate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Fetch the formatted summary
      const summaryResponse = await axios.get(`${API}/api/ers/data/baseline/${studyId}/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setBaselineSummary(summaryResponse.data);
    } catch (error) {
      console.error('Error calculating baseline:', error);
      // Fallback to simple calculation if new endpoint fails
      try {
        const ghgResponse = await axios.post(`${API}/api/ers/data/ghg-emissions/${studyId}`, {}, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        setBaselineSummary({
          calculated: true,
          energy: {
            electricity_kwh: electricityData.reduce((sum, r) => sum + (parseFloat(r.kwh_consumption) || 0), 0),
            electricity_mwh: electricityData.reduce((sum, r) => sum + (parseFloat(r.kwh_consumption) || 0), 0) / 1000,
            electricity_gj: electricityData.reduce((sum, r) => sum + (parseFloat(r.kwh_consumption) || 0), 0) * 0.0036,
            fuels_gj: fuelsData.reduce((sum, r) => sum + (parseFloat(r.quantity) || 0) * 0.036, 0),
            total_energy_gj: (electricityData.reduce((sum, r) => sum + (parseFloat(r.kwh_consumption) || 0), 0) * 0.0036) + 
                            (fuelsData.reduce((sum, r) => sum + (parseFloat(r.quantity) || 0) * 0.036, 0)),
            renewable_kwh: 0,
            renewable_percentage: 0
          },
          resources: {
            water_kl: waterData.reduce((sum, r) => sum + (parseFloat(r.quantity_kl) || 0), 0),
            waste_kg: wasteData.reduce((sum, r) => sum + (parseFloat(r.quantity) || 0), 0),
            production_mt: productionData.reduce((sum, r) => sum + (parseFloat(r.production_qty) || 0), 0)
          },
          ghg: {
            scope1_tco2e: ghgResponse.data?.scope1?.total_tco2e || 0,
            scope2_tco2e: ghgResponse.data?.scope2?.total_tco2e || 0,
            total_tco2e: ghgResponse.data?.total_ghg?.total_ghg_tco2e || 0,
            scope1_percentage: ghgResponse.data?.total_ghg?.scope1_percentage || 0,
            scope2_percentage: ghgResponse.data?.total_ghg?.scope2_percentage || 0,
            grid_ef_used: 0.716
          },
          intensities: {
            available: productionData.reduce((sum, r) => sum + (parseFloat(r.production_qty) || 0), 0) > 0
          },
          data_quality: { quality_level: 'medium', overall_score: 60 },
          region: 'India'
        });
      } catch (fallbackError) {
        console.error('Fallback calculation also failed:', fallbackError);
      }
    } finally {
      setCalculatingBaseline(false);
    }
  };

  // Generate AI narrative for baseline
  const generateAINarrative = async () => {
    setGeneratingNarrative(true);
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API}/api/ers/data/baseline/${studyId}/ai-narrative`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update baseline summary with narrative
      setBaselineSummary(prev => ({
        ...prev,
        ai_narrative: response.data.narrative,
        narrative_generated_at: response.data.generated_at
      }));
    } catch (error) {
      console.error('Error generating AI narrative:', error);
      showError('Failed to generate AI narrative. Please try again.');
    } finally {
      setGeneratingNarrative(false);
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <ProjectSetupStep 
          setup={projectSetup} 
          setSetup={setProjectSetup}
          onSave={saveProjectSetup}
          saving={saving}
        />;
      case 2:
        return <DocumentIntakeStep 
          documents={documents}
          onUpload={handleFileUpload}
          onParse={parseDocumentWithAI}
          uploading={uploadingFile}
          studyId={studyId}
        />;
      case 3:
        return <TimeSeriesStep 
          activeTab={activeDataTab}
          setActiveTab={setActiveDataTab}
          electricityData={electricityData}
          setElectricityData={setElectricityData}
          fuelsData={fuelsData}
          setFuelsData={setFuelsData}
          waterData={waterData}
          setWaterData={setWaterData}
          productionData={productionData}
          setProductionData={setProductionData}
          wasteData={wasteData}
          setWasteData={setWasteData}
          equipmentData={equipmentData}
          setEquipmentData={setEquipmentData}
          onSave={saveTimeSeriesData}
          saving={saving}
          createEmptyElectricityRow={createEmptyElectricityRow}
          createEmptyFuelsRow={createEmptyFuelsRow}
          createEmptyWaterRow={createEmptyWaterRow}
          createEmptyProductionRow={createEmptyProductionRow}
          createEmptyWasteRow={createEmptyWasteRow}
          createEmptyEquipmentRow={createEmptyEquipmentRow}
        />;
      case 4:
        return <ValidationStep 
          results={validationResults}
          onValidate={runValidation}
          validating={validating}
        />;
      case 5:
        return <GapResolutionStep 
          gaps={gaps}
          setGaps={setGaps}
          validationResults={validationResults}
          electricityData={electricityData}
          fuelsData={fuelsData}
          waterData={waterData}
          productionData={productionData}
          wasteData={wasteData}
        />;
      case 6:
        return <BaselineSummaryStep 
          summary={baselineSummary}
          onCalculate={calculateBaseline}
          onGenerateNarrative={generateAINarrative}
          loading={calculatingBaseline}
          narrativeLoading={generatingNarrative}
          study={study}
        />;
      default:
        return null;
    }
  };

  if (loading && !study) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading study data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Step Navigation */}
      <div className="bg-white rounded-xl shadow-md p-4">
        <div className="flex items-center justify-between overflow-x-auto">
          {STEPS.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            
            return (
              <React.Fragment key={step.id}>
                <button
                  onClick={() => setCurrentStep(step.id)}
                  className={`flex flex-col items-center min-w-[100px] p-3 rounded-lg transition-all ${
                    isActive 
                      ? 'bg-blue-50 text-blue-700' 
                      : isCompleted 
                        ? 'text-green-600 hover:bg-green-50' 
                        : 'text-gray-400 hover:bg-gray-50 hover:text-gray-600'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                    isActive 
                      ? 'bg-blue-600 text-white' 
                      : isCompleted 
                        ? 'bg-green-100 text-green-600' 
                        : 'bg-gray-100'
                  }`}>
                    {isCompleted ? <Check className="w-5 h-5" /> : <StepIcon className="w-5 h-5" />}
                  </div>
                  <span className="text-xs font-medium text-center">{step.name}</span>
                </button>
                {index < STEPS.length - 1 && (
                  <ChevronRight className={`w-5 h-5 flex-shrink-0 ${
                    currentStep > step.id ? 'text-green-400' : 'text-gray-300'
                  }`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Step {currentStep}: {STEPS[currentStep - 1].name}
            </h2>
            <p className="text-sm text-gray-500">{STEPS[currentStep - 1].description}</p>
          </div>
          <div className="flex gap-2">
            {currentStep > 1 && (
              <button
                onClick={() => setCurrentStep(currentStep - 1)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Previous
              </button>
            )}
            {currentStep < 6 && (
              <button
                onClick={() => setCurrentStep(currentStep + 1)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Next Step
              </button>
            )}
          </div>
        </div>
        
        {renderStepContent()}
      </div>
    </div>
  );
};

// ==================== STEP 1: PROJECT SETUP ====================
const ProjectSetupStep = ({ setup, setSetup, onSave, saving }) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Baseline Period */}
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Baseline Period
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="month"
                value={setup.baseline_start}
                onChange={(e) => setSetup({ ...setup, baseline_start: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="month"
                value={setup.baseline_end}
                onChange={(e) => setSetup({ ...setup, baseline_end: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Baseline Duration (months)
              <span className="ml-1 text-xs text-gray-500">(Recommended: 24-36)</span>
            </label>
            <select
              value={setup.baseline_months}
              onChange={(e) => setSetup({ ...setup, baseline_months: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value={12}>12 months</option>
              <option value={24}>24 months</option>
              <option value={36}>36 months</option>
            </select>
          </div>
        </div>

        {/* Reporting Boundary */}
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Settings className="w-5 h-5 text-blue-600" />
            Reporting Boundary
          </h3>
          <div className="space-y-2">
            {['scope1', 'scope2', 'scope3'].map((scope) => (
              <label key={scope} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                <input
                  type="checkbox"
                  checked={setup.reporting_boundary.includes(scope)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSetup({ ...setup, reporting_boundary: [...setup.reporting_boundary, scope] });
                    } else {
                      setSetup({ ...setup, reporting_boundary: setup.reporting_boundary.filter(s => s !== scope) });
                    }
                  }}
                  className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <span className="font-medium text-gray-900">{scope.replace('scope', 'Scope ')}</span>
                  <p className="text-xs text-gray-500">
                    {scope === 'scope1' && 'Direct emissions from fuel combustion'}
                    {scope === 'scope2' && 'Indirect emissions from purchased electricity'}
                    {scope === 'scope3' && 'Other indirect emissions (supply chain, transport)'}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <textarea
          value={setup.notes}
          onChange={(e) => setSetup({ ...setup, notes: e.target.value })}
          rows={3}
          placeholder="Any additional notes about the baseline period or scope..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={onSave}
          disabled={saving}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Setup
        </button>
      </div>
    </div>
  );
};

// ==================== STEP 2: DOCUMENT INTAKE ====================
const DocumentIntakeStep = ({ documents, onUpload, onParse, uploading, studyId }) => {
  const [dragOver, setDragOver] = useState(false);
  
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const event = { target: { files } };
      onUpload(event);
    }
  };

  return (
    <div className="space-y-6">
      {/* Upload Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
          dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <Upload className={`w-12 h-12 mx-auto mb-4 ${dragOver ? 'text-blue-500' : 'text-gray-400'}`} />
        <p className="text-lg font-medium text-gray-700 mb-2">
          Drag & drop files here
        </p>
        <p className="text-sm text-gray-500 mb-4">
          Upload utility bills, fuel logs, production records, audit reports
        </p>
        <label className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Browse Files
          <input
            type="file"
            multiple
            accept=".pdf,.xlsx,.xls,.csv,.jpg,.jpeg,.png"
            onChange={onUpload}
            className="hidden"
          />
        </label>
        <p className="text-xs text-gray-400 mt-2">
          Supported: PDF, Excel, CSV, Images
        </p>
      </div>

      {/* Document List */}
      {documents.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-semibold text-gray-900">Uploaded Documents ({documents.length})</h3>
          <div className="divide-y divide-gray-200 border border-gray-200 rounded-lg">
            {documents.map((doc, idx) => (
              <div key={doc.id || idx} className="p-4 flex items-center justify-between hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <FileText className="w-8 h-8 text-blue-500" />
                  <div>
                    <p className="font-medium text-gray-900">{doc.filename || doc.name}</p>
                    <p className="text-xs text-gray-500">
                      {doc.type || 'Document'} • {doc.period || 'No period set'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {doc.parsed ? (
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                      Parsed
                    </span>
                  ) : (
                    <button
                      onClick={() => onParse(doc.id)}
                      className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-200 flex items-center gap-1"
                    >
                      <Cpu className="w-3 h-3" />
                      AI Parse
                    </button>
                  )}
                  <button className="p-2 text-gray-400 hover:text-gray-600">
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900">AI Bill Parsing</h4>
            <p className="text-sm text-blue-700 mt-1">
              Click &quot;AI Parse&quot; on uploaded documents to automatically extract consumption data, 
              meter IDs, and billing information. Parsed data will be auto-filled in the Time-Series step.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== STEP 3: TIME-SERIES DATA ====================
const TimeSeriesStep = ({ 
  activeTab, setActiveTab,
  electricityData, setElectricityData,
  fuelsData, setFuelsData,
  waterData, setWaterData,
  productionData, setProductionData,
  wasteData, setWasteData,
  equipmentData, setEquipmentData,
  onSave, saving,
  createEmptyElectricityRow, createEmptyFuelsRow, createEmptyWaterRow,
  createEmptyProductionRow, createEmptyWasteRow, createEmptyEquipmentRow
}) => {
  
  // CSV Templates for each data type
  const CSV_TEMPLATES = {
    electricity: 'period,meter_id,kwh_consumption,renewable_kwh,max_demand_kva,power_factor,bill_amount,comment\n2024-01,MTR-MAIN,10000,500,150,0.92,75000,Sample data',
    fuels: 'period,fuel_type,quantity,unit,bill_amount,comment\n2024-01,Diesel,1000,L,85000,Sample data',
    water: 'period,source_type,quantity_kl,bill_amount,comment\n2024-01,Municipal,500,15000,Sample data',
    production: 'period,product_id,production_qty,unit\n2024-01,TotalOutput,1000,MT',
    waste: 'period,waste_type,quantity,unit,disposal_method\n2024-01,Non-Hazardous,500,kg,Recycled',
    equipment: 'equipment_id,equipment_type,location,rated_power_kw,qty,control_type,operating_hours,load_percent,notes\nEQ-001,Compressor,Utilities,75,2,VFD,8000,70,Air compressor'
  };

  // Download CSV template
  const downloadTemplate = () => {
    const template = CSV_TEMPLATES[activeTab];
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeTab}_template.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Import CSV file
  const handleCSVImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        showWarning('CSV file is empty or has no data rows');
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim());
      const newData = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const row = { id: `import_${Date.now()}_${i}` };
        headers.forEach((header, idx) => {
          row[header] = values[idx] || '';
        });
        newData.push(row);
      }

      // Set data based on active tab
      switch (activeTab) {
        case 'electricity': setElectricityData(newData); break;
        case 'fuels': setFuelsData(newData); break;
        case 'water': setWaterData(newData); break;
        case 'production': setProductionData(newData); break;
        case 'waste': setWasteData(newData); break;
        case 'equipment': setEquipmentData(newData); break;
        default: break;
      }

      showSuccess(`Imported ${newData.length} rows successfully!`);
    };
    reader.readAsText(file);
    event.target.value = ''; // Reset input
  };

  const renderDataTable = () => {
    switch (activeTab) {
      case 'electricity':
        return <ElectricityTable 
          data={electricityData} 
          setData={setElectricityData}
          createEmptyRow={createEmptyElectricityRow}
        />;
      case 'fuels':
        return <FuelsTable 
          data={fuelsData} 
          setData={setFuelsData}
          createEmptyRow={createEmptyFuelsRow}
        />;
      case 'water':
        return <WaterTable 
          data={waterData} 
          setData={setWaterData}
          createEmptyRow={createEmptyWaterRow}
        />;
      case 'production':
        return <ProductionTable 
          data={productionData} 
          setData={setProductionData}
          createEmptyRow={createEmptyProductionRow}
        />;
      case 'waste':
        return <WasteTable 
          data={wasteData} 
          setData={setWasteData}
          createEmptyRow={createEmptyWasteRow}
        />;
      case 'equipment':
        return <EquipmentTable 
          data={equipmentData} 
          setData={setEquipmentData}
          createEmptyRow={createEmptyEquipmentRow}
        />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Data Type Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-3">
        {DATA_TABS.map((tab) => {
          const TabIcon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? `bg-${tab.color}-100 text-${tab.color}-700 border border-${tab.color}-300`
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <TabIcon className="w-4 h-4" />
              {tab.name}
            </button>
          );
        })}
      </div>

      {/* Action Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button 
            onClick={downloadTemplate}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50"
          >
            <Download className="w-4 h-4" />
            Download Template
          </button>
          <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 cursor-pointer">
            <FileSpreadsheet className="w-4 h-4" />
            Import CSV
            <input 
              type="file" 
              accept=".csv" 
              className="hidden" 
              onChange={handleCSVImport}
            />
          </label>
        </div>
        <button
          onClick={() => onSave(activeTab, 
            activeTab === 'electricity' ? electricityData :
            activeTab === 'fuels' ? fuelsData :
            activeTab === 'water' ? waterData :
            activeTab === 'production' ? productionData :
            activeTab === 'waste' ? wasteData :
            equipmentData
          )}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save {DATA_TABS.find(t => t.id === activeTab)?.name}
        </button>
      </div>

      {/* Data Table */}
      <div className="overflow-x-auto">
        {renderDataTable()}
      </div>
    </div>
  );
};

// ==================== DATA TABLES ====================
const ElectricityTable = ({ data, setData, createEmptyRow }) => {
  const updateRow = (index, field, value) => {
    const newData = [...data];
    newData[index] = { ...newData[index], [field]: value };
    setData(newData);
  };

  const addRow = () => setData([...data, createEmptyRow()]);
  const removeRow = (index) => setData(data.filter((_, i) => i !== index));

  return (
    <div>
      <table className="w-full text-sm">
        <thead className="bg-yellow-50">
          <tr>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Period</th>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Meter ID</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-700">
              kWh
              <span className="ml-1 cursor-help text-gray-400" title="Monthly electricity consumption">
                <Info className="w-3 h-3 inline" />
              </span>
            </th>
            <th className="px-3 py-3 text-right font-semibold text-gray-700">
              <span className="flex items-center justify-end gap-1">
                Renewable (kWh)
                <span className="text-xs text-green-500">🌱</span>
              </span>
            </th>
            <th className="px-3 py-3 text-right font-semibold text-gray-700">Max Demand (kVA)</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-700">Power Factor</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-700">Bill Amount</th>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Comment</th>
            <th className="px-3 py-3 text-center font-semibold text-gray-700">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {data.map((row, idx) => (
            <tr key={row.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-3 py-2">
                <input
                  type="month"
                  value={row.period || ''}
                  onChange={(e) => updateRow(idx, 'period', e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                />
              </td>
              <td className="px-3 py-2">
                <input
                  type="text"
                  value={row.meter_id || ''}
                  onChange={(e) => updateRow(idx, 'meter_id', e.target.value)}
                  placeholder="MTR-MAIN"
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                />
              </td>
              <td className="px-3 py-2">
                <input
                  type="number"
                  value={row.kwh_consumption || ''}
                  onChange={(e) => updateRow(idx, 'kwh_consumption', e.target.value)}
                  placeholder="0"
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right"
                />
              </td>
              <td className="px-3 py-2">
                <input
                  type="number"
                  value={row.renewable_kwh || ''}
                  onChange={(e) => updateRow(idx, 'renewable_kwh', e.target.value)}
                  placeholder="0"
                  className="w-full px-2 py-1 border border-green-300 bg-green-50 rounded text-sm text-right"
                />
              </td>
              <td className="px-3 py-2">
                <input
                  type="number"
                  value={row.max_demand_kva || ''}
                  onChange={(e) => updateRow(idx, 'max_demand_kva', e.target.value)}
                  placeholder="0"
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right"
                />
              </td>
              <td className="px-3 py-2">
                <input
                  type="number"
                  step="0.01"
                  value={row.power_factor || ''}
                  onChange={(e) => updateRow(idx, 'power_factor', e.target.value)}
                  placeholder="0.90"
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right"
                />
              </td>
              <td className="px-3 py-2">
                <input
                  type="number"
                  value={row.bill_amount || ''}
                  onChange={(e) => updateRow(idx, 'bill_amount', e.target.value)}
                  placeholder="0"
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right"
                />
              </td>
              <td className="px-3 py-2">
                <input
                  type="text"
                  value={row.comment || ''}
                  onChange={(e) => updateRow(idx, 'comment', e.target.value)}
                  placeholder="Optional"
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                />
              </td>
              <td className="px-3 py-2 text-center">
                <button
                  onClick={() => removeRow(idx)}
                  className="p-1 text-red-500 hover:bg-red-50 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        onClick={addRow}
        className="mt-4 flex items-center gap-2 px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200"
      >
        <Plus className="w-4 h-4" />
        Add Row
      </button>
    </div>
  );
};

// Similar tables for Fuels, Water, Production, Waste, Equipment...
const FuelsTable = ({ data, setData, createEmptyRow }) => {
  const updateRow = (index, field, value) => {
    const newData = [...data];
    newData[index] = { ...newData[index], [field]: value };
    setData(newData);
  };

  const addRow = () => setData([...data, createEmptyRow()]);
  const removeRow = (index) => setData(data.filter((_, i) => i !== index));

  return (
    <div>
      <table className="w-full text-sm">
        <thead className="bg-orange-50">
          <tr>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Period</th>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Fuel Type</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-700">Quantity</th>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Unit</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-700">Bill Amount</th>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Comment</th>
            <th className="px-3 py-3 text-center font-semibold text-gray-700">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {data.map((row, idx) => (
            <tr key={row.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-3 py-2">
                <input type="month" value={row.period || ''} onChange={(e) => updateRow(idx, 'period', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
              </td>
              <td className="px-3 py-2">
                <select value={row.fuel_type || ''} onChange={(e) => updateRow(idx, 'fuel_type', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm">
                  {FUEL_TYPES.map(ft => <option key={ft} value={ft}>{ft}</option>)}
                </select>
              </td>
              <td className="px-3 py-2">
                <input type="number" value={row.quantity || ''} onChange={(e) => updateRow(idx, 'quantity', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right" />
              </td>
              <td className="px-3 py-2">
                <select value={row.unit || 'L'} onChange={(e) => updateRow(idx, 'unit', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm">
                  <option value="L">Liters</option>
                  <option value="KG">KG</option>
                  <option value="SCM">SCM</option>
                  <option value="MT">MT</option>
                </select>
              </td>
              <td className="px-3 py-2">
                <input type="number" value={row.bill_amount || ''} onChange={(e) => updateRow(idx, 'bill_amount', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right" />
              </td>
              <td className="px-3 py-2">
                <input type="text" value={row.comment || ''} onChange={(e) => updateRow(idx, 'comment', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
              </td>
              <td className="px-3 py-2 text-center">
                <button onClick={() => removeRow(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button onClick={addRow} className="mt-4 flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200">
        <Plus className="w-4 h-4" /> Add Row
      </button>
    </div>
  );
};

const WaterTable = ({ data, setData, createEmptyRow }) => {
  const updateRow = (index, field, value) => {
    const newData = [...data];
    newData[index] = { ...newData[index], [field]: value };
    setData(newData);
  };

  const addRow = () => setData([...data, createEmptyRow()]);
  const removeRow = (index) => setData(data.filter((_, i) => i !== index));

  return (
    <div>
      <table className="w-full text-sm">
        <thead className="bg-blue-50">
          <tr>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Period</th>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Source Type</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-700">Quantity (KL)</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-700">Bill Amount</th>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Comment</th>
            <th className="px-3 py-3 text-center font-semibold text-gray-700">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {data.map((row, idx) => (
            <tr key={row.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-3 py-2">
                <input type="month" value={row.period || ''} onChange={(e) => updateRow(idx, 'period', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
              </td>
              <td className="px-3 py-2">
                <select value={row.source_type || ''} onChange={(e) => updateRow(idx, 'source_type', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm">
                  {WATER_SOURCES.map(ws => <option key={ws} value={ws}>{ws}</option>)}
                </select>
              </td>
              <td className="px-3 py-2">
                <input type="number" value={row.quantity_kl || ''} onChange={(e) => updateRow(idx, 'quantity_kl', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right" />
              </td>
              <td className="px-3 py-2">
                <input type="number" value={row.bill_amount || ''} onChange={(e) => updateRow(idx, 'bill_amount', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right" />
              </td>
              <td className="px-3 py-2">
                <input type="text" value={row.comment || ''} onChange={(e) => updateRow(idx, 'comment', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
              </td>
              <td className="px-3 py-2 text-center">
                <button onClick={() => removeRow(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button onClick={addRow} className="mt-4 flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200">
        <Plus className="w-4 h-4" /> Add Row
      </button>
    </div>
  );
};

const ProductionTable = ({ data, setData, createEmptyRow }) => {
  const updateRow = (index, field, value) => {
    const newData = [...data];
    newData[index] = { ...newData[index], [field]: value };
    setData(newData);
  };

  const addRow = () => setData([...data, createEmptyRow()]);
  const removeRow = (index) => setData(data.filter((_, i) => i !== index));

  return (
    <div>
      <table className="w-full text-sm">
        <thead className="bg-green-50">
          <tr>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Period</th>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Product ID</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-700">Production Qty</th>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Unit</th>
            <th className="px-3 py-3 text-center font-semibold text-gray-700">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {data.map((row, idx) => (
            <tr key={row.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-3 py-2">
                <input type="month" value={row.period || ''} onChange={(e) => updateRow(idx, 'period', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
              </td>
              <td className="px-3 py-2">
                <input type="text" value={row.product_id || ''} onChange={(e) => updateRow(idx, 'product_id', e.target.value)} placeholder="TotalOutput" className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
              </td>
              <td className="px-3 py-2">
                <input type="number" value={row.production_qty || ''} onChange={(e) => updateRow(idx, 'production_qty', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right" />
              </td>
              <td className="px-3 py-2">
                <select value={row.unit || 'MT'} onChange={(e) => updateRow(idx, 'unit', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm">
                  <option value="MT">MT</option>
                  <option value="Units">Units</option>
                  <option value="KL">KL</option>
                </select>
              </td>
              <td className="px-3 py-2 text-center">
                <button onClick={() => removeRow(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button onClick={addRow} className="mt-4 flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200">
        <Plus className="w-4 h-4" /> Add Row
      </button>
    </div>
  );
};

const WasteTable = ({ data, setData, createEmptyRow }) => {
  const updateRow = (index, field, value) => {
    const newData = [...data];
    newData[index] = { ...newData[index], [field]: value };
    setData(newData);
  };

  const addRow = () => setData([...data, createEmptyRow()]);
  const removeRow = (index) => setData(data.filter((_, i) => i !== index));

  return (
    <div>
      <table className="w-full text-sm">
        <thead className="bg-gray-100">
          <tr>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Period</th>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Waste Type</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-700">Quantity</th>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Unit</th>
            <th className="px-3 py-3 text-left font-semibold text-gray-700">Disposal Method</th>
            <th className="px-3 py-3 text-center font-semibold text-gray-700">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {data.map((row, idx) => (
            <tr key={row.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-3 py-2">
                <input type="month" value={row.period || ''} onChange={(e) => updateRow(idx, 'period', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
              </td>
              <td className="px-3 py-2">
                <select value={row.waste_type || ''} onChange={(e) => updateRow(idx, 'waste_type', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm">
                  {WASTE_TYPES.map(wt => <option key={wt} value={wt}>{wt}</option>)}
                </select>
              </td>
              <td className="px-3 py-2">
                <input type="number" value={row.quantity || ''} onChange={(e) => updateRow(idx, 'quantity', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right" />
              </td>
              <td className="px-3 py-2">
                <select value={row.unit || 'MT'} onChange={(e) => updateRow(idx, 'unit', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm">
                  <option value="MT">MT</option>
                  <option value="KG">KG</option>
                  <option value="L">L</option>
                </select>
              </td>
              <td className="px-3 py-2">
                <input type="text" value={row.disposal_method || ''} onChange={(e) => updateRow(idx, 'disposal_method', e.target.value)} placeholder="Landfill, Incineration..." className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
              </td>
              <td className="px-3 py-2 text-center">
                <button onClick={() => removeRow(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button onClick={addRow} className="mt-4 flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
        <Plus className="w-4 h-4" /> Add Row
      </button>
    </div>
  );
};

const EquipmentTable = ({ data, setData, createEmptyRow }) => {
  const updateRow = (index, field, value) => {
    const newData = [...data];
    newData[index] = { ...newData[index], [field]: value };
    setData(newData);
  };

  const addRow = () => setData([...data, createEmptyRow()]);
  const removeRow = (index) => setData(data.filter((_, i) => i !== index));

  return (
    <div>
      <table className="w-full text-sm">
        <thead className="bg-purple-50">
          <tr>
            <th className="px-2 py-3 text-left font-semibold text-gray-700">Equipment ID</th>
            <th className="px-2 py-3 text-left font-semibold text-gray-700">Type</th>
            <th className="px-2 py-3 text-left font-semibold text-gray-700">Location</th>
            <th className="px-2 py-3 text-right font-semibold text-gray-700">Power (kW)</th>
            <th className="px-2 py-3 text-center font-semibold text-gray-700">Qty</th>
            <th className="px-2 py-3 text-left font-semibold text-gray-700">Control</th>
            <th className="px-2 py-3 text-right font-semibold text-gray-700">Hrs/Day</th>
            <th className="px-2 py-3 text-right font-semibold text-gray-700">Load %</th>
            <th className="px-2 py-3 text-left font-semibold text-gray-700">Notes</th>
            <th className="px-2 py-3 text-center font-semibold text-gray-700">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {data.map((row, idx) => (
            <tr key={row.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-2 py-2">
                <input type="text" value={row.equipment_id || ''} onChange={(e) => updateRow(idx, 'equipment_id', e.target.value)} placeholder="EQP-001" className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
              </td>
              <td className="px-2 py-2">
                <input type="text" value={row.equipment_type || ''} onChange={(e) => updateRow(idx, 'equipment_type', e.target.value)} placeholder="Compressor" className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
              </td>
              <td className="px-2 py-2">
                <input type="text" value={row.location || ''} onChange={(e) => updateRow(idx, 'location', e.target.value)} placeholder="Utilities" className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
              </td>
              <td className="px-2 py-2">
                <input type="number" value={row.rated_power_kw || ''} onChange={(e) => updateRow(idx, 'rated_power_kw', e.target.value)} className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right" />
              </td>
              <td className="px-2 py-2">
                <input type="number" value={row.qty || 1} onChange={(e) => updateRow(idx, 'qty', e.target.value)} className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center" />
              </td>
              <td className="px-2 py-2">
                <select value={row.control_type || ''} onChange={(e) => updateRow(idx, 'control_type', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm">
                  {CONTROL_TYPES.map(ct => <option key={ct} value={ct}>{ct}</option>)}
                </select>
              </td>
              <td className="px-2 py-2">
                <input type="number" value={row.operating_hours || ''} onChange={(e) => updateRow(idx, 'operating_hours', e.target.value)} className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-right" />
              </td>
              <td className="px-2 py-2">
                <input type="number" value={row.load_percent || ''} onChange={(e) => updateRow(idx, 'load_percent', e.target.value)} className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-right" />
              </td>
              <td className="px-2 py-2">
                <input type="text" value={row.notes || ''} onChange={(e) => updateRow(idx, 'notes', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
              </td>
              <td className="px-2 py-2 text-center">
                <button onClick={() => removeRow(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button onClick={addRow} className="mt-4 flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200">
        <Plus className="w-4 h-4" /> Add Row
      </button>
    </div>
  );
};

// ==================== STEP 4: VALIDATION ====================
const ValidationStep = ({ results, onValidate, validating }) => {
  const [expandedCard, setExpandedCard] = useState(null);

  const toggleCard = (cardType) => {
    setExpandedCard(expandedCard === cardType ? null : cardType);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-900">Data Validation</h3>
          <p className="text-sm text-gray-500">Run automated checks on your data - click cards to see details</p>
        </div>
        <button
          onClick={onValidate}
          disabled={validating}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
        >
          {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Run Validation
        </button>
      </div>

      {results ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Total Issues Card */}
            <div 
              onClick={() => toggleCard('total')}
              className={`bg-white border-2 rounded-lg p-4 cursor-pointer transition-all ${
                expandedCard === 'total' ? 'border-red-400 shadow-lg' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${results.total_issues > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                  {results.total_issues > 0 ? <AlertCircle className="w-5 h-5 text-red-600" /> : <CheckCircle className="w-5 h-5 text-green-600" />}
                </div>
                <div>
                  <p className="text-2xl font-bold">{results.total_issues}</p>
                  <p className="text-xs text-gray-500">Total Issues</p>
                </div>
                <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${expandedCard === 'total' ? 'rotate-180' : ''}`} />
              </div>
            </div>
            
            {/* Missing Months Card */}
            <div 
              onClick={() => toggleCard('missing')}
              className={`bg-white border-2 rounded-lg p-4 cursor-pointer transition-all ${
                expandedCard === 'missing' ? 'border-amber-400 shadow-lg' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Calendar className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{results.missing_months}</p>
                  <p className="text-xs text-gray-500">Missing Months</p>
                </div>
                <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${expandedCard === 'missing' ? 'rotate-180' : ''}`} />
              </div>
            </div>
            
            {/* Outliers Card */}
            <div 
              onClick={() => toggleCard('outliers')}
              className={`bg-white border-2 rounded-lg p-4 cursor-pointer transition-all ${
                expandedCard === 'outliers' ? 'border-orange-400 shadow-lg' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{results.outliers}</p>
                  <p className="text-xs text-gray-500">Outliers Detected</p>
                </div>
                <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${expandedCard === 'outliers' ? 'rotate-180' : ''}`} />
              </div>
            </div>
            
            {/* Unit Issues Card */}
            <div 
              onClick={() => toggleCard('units')}
              className={`bg-white border-2 rounded-lg p-4 cursor-pointer transition-all ${
                expandedCard === 'units' ? 'border-blue-400 shadow-lg' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Settings className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{results.unit_issues}</p>
                  <p className="text-xs text-gray-500">Unit Issues</p>
                </div>
                <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${expandedCard === 'units' ? 'rotate-180' : ''}`} />
              </div>
            </div>
          </div>

          {/* Expanded Details Panel */}
          {expandedCard && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                {expandedCard === 'total' && <><AlertCircle className="w-4 h-4 text-red-500" /> All Issues</>}
                {expandedCard === 'missing' && <><Calendar className="w-4 h-4 text-amber-500" /> Missing Data Details</>}
                {expandedCard === 'outliers' && <><AlertTriangle className="w-4 h-4 text-orange-500" /> Outlier Details</>}
                {expandedCard === 'units' && <><Settings className="w-4 h-4 text-blue-500" /> Unit Inconsistencies</>}
              </h4>
              
              {results.issues && results.issues.length > 0 ? (
                <ul className="space-y-2">
                  {results.issues
                    .filter(issue => {
                      if (expandedCard === 'total') return true;
                      if (expandedCard === 'missing') return issue.type === 'missing' || issue.type === 'gap';
                      if (expandedCard === 'outliers') return issue.type === 'outlier';
                      if (expandedCard === 'units') return issue.type === 'unit';
                      return true;
                    })
                    .map((issue, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm p-2 bg-gray-50 rounded">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          issue.severity === 'critical' ? 'bg-red-100 text-red-700' :
                          issue.severity === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {issue.severity || 'info'}
                        </span>
                        <span className="text-gray-700">
                          <span className="font-medium">{issue.field || issue.type}:</span> {issue.message || issue.description}
                          {issue.period && <span className="text-gray-500 ml-1">({issue.period})</span>}
                        </span>
                      </li>
                    ))
                  }
                </ul>
              ) : (
                <p className="text-gray-500 text-sm">
                  {expandedCard === 'total' && results.total_issues === 0 && 'No issues found - your data looks good!'}
                  {expandedCard === 'missing' && results.missing_months === 0 && 'No missing months detected.'}
                  {expandedCard === 'outliers' && results.outliers === 0 && 'No outliers detected in your data.'}
                  {expandedCard === 'units' && results.unit_issues === 0 && 'All units are consistent.'}
                </p>
              )}
              
              {/* Show gaps if available */}
              {expandedCard === 'missing' && results.gaps && results.gaps.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-sm font-medium text-gray-700 mb-2">Data Gaps:</p>
                  <ul className="space-y-1">
                    {results.gaps.map((gap, idx) => (
                      <li key={idx} className="text-sm text-gray-600 flex items-center gap-2">
                        <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
                        {gap.type}: {gap.period} - {gap.description}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Summary */}
          {results.total_issues === 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-medium text-green-800">All validations passed!</p>
                <p className="text-sm text-green-600 mt-1">Your data is complete for the baseline period.</p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <CheckCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Click &quot;Run Validation&quot; to check your data for issues</p>
        </div>
      )}
    </div>
  );
};

// ==================== STEP 5: GAP RESOLUTION ====================
const GapResolutionStep = ({ gaps, setGaps, validationResults, electricityData, fuelsData, waterData, productionData, wasteData }) => {
  const [resolving, setResolving] = useState(false);

  // Detect gaps from validation results and actual data
  const detectGaps = () => {
    const detectedGaps = [];
    
    // Check for missing months in electricity data
    const elecPeriods = electricityData.filter(r => r.period).map(r => r.period);
    if (elecPeriods.length > 0 && elecPeriods.length < 12) {
      detectedGaps.push({
        type: 'Electricity',
        period: `${elecPeriods.length} of 12 months`,
        description: `Missing ${12 - elecPeriods.length} months of electricity data`,
        severity: elecPeriods.length < 6 ? 'critical' : 'warning',
        canInterpolate: elecPeriods.length >= 6
      });
    }

    // Check production data
    const prodPeriods = productionData.filter(r => r.period && r.production_qty).map(r => r.period);
    if (prodPeriods.length === 0) {
      detectedGaps.push({
        type: 'Production',
        period: 'All periods',
        description: 'No production data entered - intensity calculations not possible',
        severity: 'critical',
        canInterpolate: false
      });
    } else if (prodPeriods.length < 12) {
      detectedGaps.push({
        type: 'Production',
        period: `${prodPeriods.length} of 12 months`,
        description: `Missing ${12 - prodPeriods.length} months of production data`,
        severity: 'warning',
        canInterpolate: false
      });
    }

    // Check water data
    const waterPeriods = waterData.filter(r => r.period && r.quantity_kl).map(r => r.period);
    if (waterPeriods.length > 0 && waterPeriods.length < 12) {
      detectedGaps.push({
        type: 'Water',
        period: `${waterPeriods.length} of 12 months`,
        description: `Missing ${12 - waterPeriods.length} months of water data`,
        severity: 'info',
        canInterpolate: (12 - waterPeriods.length) <= 2
      });
    }

    // Check fuel data
    const fuelPeriods = fuelsData.filter(r => r.period && r.quantity).map(r => r.period);
    if (fuelPeriods.length === 0 && fuelsData.length <= 1) {
      detectedGaps.push({
        type: 'Fuels',
        period: 'All periods',
        description: 'No fuel data entered (skip if facility has no fuel consumption)',
        severity: 'info',
        canInterpolate: false
      });
    }

    // Add gaps from validation results
    if (validationResults?.gaps) {
      validationResults.gaps.forEach(gap => {
        if (!detectedGaps.find(g => g.type === gap.type)) {
          detectedGaps.push({
            ...gap,
            canInterpolate: gap.type !== 'Production' && gap.type !== 'Fuels'
          });
        }
      });
    }

    return detectedGaps;
  };

  const allGaps = gaps.length > 0 ? gaps : detectGaps();

  const handleInterpolate = (gapIndex) => {
    setResolving(true);
    // Simulate interpolation - in real implementation, this would calculate values
    setTimeout(() => {
      const newGaps = [...allGaps];
      newGaps[gapIndex] = { ...newGaps[gapIndex], resolved: true, method: 'interpolated' };
      setGaps(newGaps);
      setResolving(false);
    }, 500);
  };

  const handleMarkNA = (gapIndex) => {
    const newGaps = [...allGaps];
    newGaps[gapIndex] = { ...newGaps[gapIndex], resolved: true, method: 'marked_na' };
    setGaps(newGaps);
  };

  const unresolvedGaps = allGaps.filter(g => !g.resolved);
  const resolvedGaps = allGaps.filter(g => g.resolved);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-900">Gap Resolution</h3>
          <p className="text-sm text-gray-500">Review and resolve data gaps before baseline calculation</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-xs ${
            unresolvedGaps.length === 0 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
          }`}>
            {unresolvedGaps.length} unresolved
          </span>
          {resolvedGaps.length > 0 && (
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs">
              {resolvedGaps.length} resolved
            </span>
          )}
        </div>
      </div>

      {unresolvedGaps.length > 0 ? (
        <div className="space-y-4">
          {unresolvedGaps.map((gap, idx) => (
            <div key={idx} className={`border rounded-lg p-4 ${
              gap.severity === 'critical' ? 'bg-red-50 border-red-200' :
              gap.severity === 'warning' ? 'bg-amber-50 border-amber-200' :
              'bg-blue-50 border-blue-200'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertTriangle className={`w-5 h-5 ${
                    gap.severity === 'critical' ? 'text-red-600' :
                    gap.severity === 'warning' ? 'text-amber-600' :
                    'text-blue-600'
                  }`} />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900">{gap.type}</p>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        gap.severity === 'critical' ? 'bg-red-100 text-red-700' :
                        gap.severity === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {gap.severity}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{gap.period}</p>
                    <p className="text-sm text-gray-500">{gap.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {gap.canInterpolate && (
                    <button 
                      onClick={() => handleInterpolate(allGaps.indexOf(gap))}
                      disabled={resolving}
                      className="px-3 py-1 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                    >
                      {resolving ? 'Processing...' : 'Interpolate'}
                    </button>
                  )}
                  <button 
                    onClick={() => handleMarkNA(allGaps.indexOf(gap))}
                    className="px-3 py-1 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300"
                  >
                    Mark N/A
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <p className="text-green-700 font-medium">
            {allGaps.length === 0 ? 'No data gaps detected!' : 'All gaps resolved!'}
          </p>
          <p className="text-sm text-green-600 mt-1">
            {allGaps.length === 0 
              ? 'Your data is complete for the baseline period.' 
              : 'You can proceed to baseline calculation.'}
          </p>
        </div>
      )}

      {/* Show resolved gaps */}
      {resolvedGaps.length > 0 && (
        <div className="border-t pt-4">
          <p className="text-sm font-medium text-gray-700 mb-3">Resolved Gaps:</p>
          <div className="space-y-2">
            {resolvedGaps.map((gap, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 p-2 rounded">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>{gap.type}: {gap.method === 'interpolated' ? 'Interpolated' : 'Marked as N/A'}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== STEP 6: BASELINE SUMMARY ====================
const BaselineSummaryStep = ({ summary, onCalculate, onGenerateNarrative, loading, narrativeLoading, study }) => {
  const [showNarrative, setShowNarrative] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    energy: true,
    ghg: true,
    intensities: true,
    dataQuality: false,
    fuelBreakdown: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Format large numbers
  const formatNumber = (num, decimals = 0) => {
    if (!num && num !== 0) return '—';
    return num.toLocaleString('en-IN', { maximumFractionDigits: decimals });
  };

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-900 text-lg">Baseline Calculations</h3>
          <p className="text-sm text-gray-500">
            Energy totals, GHG emissions, intensities, and data quality analysis
          </p>
        </div>
        <div className="flex items-center gap-3">
          {summary?.calculated && (
            <button
              onClick={onGenerateNarrative}
              disabled={narrativeLoading}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {narrativeLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              AI Narrative
            </button>
          )}
          <button
            onClick={onCalculate}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
            {summary?.calculated ? 'Recalculate' : 'Calculate Baseline'}
          </button>
        </div>
      </div>

      {summary?.calculated ? (
        <div className="space-y-6">
          {/* AI Narrative Section */}
          {/* AI Narrative Section */}
          {summary.ai_narrative ? (
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  <h4 className="font-semibold text-gray-900">AI Baseline Narrative</h4>
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                    Generated {summary.narrative_generated_at ? new Date(summary.narrative_generated_at).toLocaleDateString() : ''}
                  </span>
                </div>
                <button 
                  onClick={() => setShowNarrative(!showNarrative)}
                  className="text-purple-600 hover:text-purple-800 text-sm"
                >
                  {showNarrative ? 'Collapse' : 'Expand'}
                </button>
              </div>
              {showNarrative && (
                <div className="prose prose-sm max-w-none text-gray-700"
                  dangerouslySetInnerHTML={{ 
                    __html: summary.ai_narrative
                      // Convert markdown headers
                      .replace(/^####\s+(.+)$/gm, '<h4 class="text-base font-semibold text-gray-800 mt-4 mb-2">$1</h4>')
                      .replace(/^###\s+(.+)$/gm, '<h3 class="text-lg font-semibold text-gray-900 mt-4 mb-2">$1</h3>')
                      // Convert bold text
                      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
                      // Convert line breaks to paragraphs
                      .split('\n\n').filter(p => p.trim()).map(p => {
                        if (p.startsWith('<h')) return p;
                        return `<p class="mb-3">${p}</p>`;
                      }).join('')
                  }}
                />
              )}
              {!showNarrative && (
                <p className="text-gray-600 text-sm line-clamp-3"
                  dangerouslySetInnerHTML={{ 
                    __html: summary.ai_narrative.substring(0, 300)
                      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>') + '...'
                  }}
                />
              )}
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <div className="flex items-center gap-3">
                <Sparkles className="w-6 h-6 text-amber-600" />
                <div className="flex-1">
                  <h4 className="font-semibold text-amber-800">AI Narrative Not Generated</h4>
                  <p className="text-sm text-amber-600 mt-1">
                    Click &quot;AI Narrative&quot; to generate an executive summary based on your baseline data.
                  </p>
                </div>
                <button
                  onClick={onGenerateNarrative}
                  disabled={narrativeLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  {narrativeLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  Generate
                </button>
              </div>
            </div>
          )}

          {/* Energy Totals Section */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <button 
              onClick={() => toggleSection('energy')}
              className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100"
            >
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-600" />
                <span className="font-semibold text-gray-900">Energy Consumption</span>
              </div>
              {expandedSections.energy ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </button>
            {expandedSections.energy && (
              <div className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-4 h-4 text-yellow-600" />
                      <span className="text-sm text-gray-600">Electricity</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatNumber(summary.energy?.electricity_mwh, 1)} <span className="text-sm font-normal">MWh</span>
                    </p>
                    <p className="text-xs text-gray-500">{formatNumber(summary.energy?.electricity_kwh)} kWh</p>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Flame className="w-4 h-4 text-orange-600" />
                      <span className="text-sm text-gray-600">Fuels</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatNumber(summary.energy?.fuels_gj, 1)} <span className="text-sm font-normal">GJ</span>
                    </p>
                    <p className="text-xs text-gray-500">{formatNumber(summary.energy?.fuels_gj / 1000, 2)} TJ</p>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Activity className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-gray-600">Total Energy</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatNumber(summary.energy?.total_energy_gj, 1)} <span className="text-sm font-normal">GJ</span>
                    </p>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm text-gray-600">Renewable</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatNumber(summary.energy?.renewable_percentage, 1)}<span className="text-sm font-normal">%</span>
                    </p>
                    <p className="text-xs text-gray-500">{formatNumber(summary.energy?.renewable_kwh)} kWh</p>
                  </div>
                </div>
                {/* Resource Totals */}
                <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-200">
                  <div className="text-center">
                    <Droplets className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                    <p className="text-lg font-semibold text-gray-900">{formatNumber(summary.resources?.water_kl)} KL</p>
                    <p className="text-xs text-gray-500">Water</p>
                  </div>
                  <div className="text-center">
                    <Factory className="w-5 h-5 text-green-500 mx-auto mb-1" />
                    <p className="text-lg font-semibold text-gray-900">{formatNumber(summary.resources?.production_mt)} MT</p>
                    <p className="text-xs text-gray-500">Production</p>
                  </div>
                  <div className="text-center">
                    <Recycle className="w-5 h-5 text-gray-500 mx-auto mb-1" />
                    <p className="text-lg font-semibold text-gray-900">{formatNumber(summary.resources?.waste_kg)} kg</p>
                    <p className="text-xs text-gray-500">Waste</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* GHG Emissions Section */}
          <div className="bg-slate-800 rounded-xl overflow-hidden">
            <button 
              onClick={() => toggleSection('ghg')}
              className="w-full flex items-center justify-between p-4 bg-slate-700 hover:bg-slate-600"
            >
              <div className="flex items-center gap-2">
                <PieChart className="w-5 h-5 text-green-400" />
                <span className="font-semibold text-white">GHG Emissions Summary</span>
              </div>
              {expandedSections.ghg ? <ChevronDown className="w-5 h-5 text-white" /> : <ChevronRight className="w-5 h-5 text-white" />}
            </button>
            {expandedSections.ghg && (
              <div className="p-6">
                <div className="grid grid-cols-3 gap-6 mb-6">
                  <div className="text-center">
                    <p className="text-slate-400 text-sm mb-1">Scope 1 (Direct)</p>
                    <p className="text-3xl font-bold text-white">{formatNumber(summary.ghg?.scope1_tco2e, 1)}</p>
                    <p className="text-slate-500 text-xs">tCO₂e</p>
                    <div className="mt-2 bg-slate-700 rounded-full h-2">
                      <div 
                        className="bg-orange-500 h-2 rounded-full"
                        style={{ width: `${summary.ghg?.scope1_percentage || 0}%` }}
                      />
                    </div>
                    <p className="text-slate-400 text-xs mt-1">{formatNumber(summary.ghg?.scope1_percentage, 1)}%</p>
                  </div>
                  <div className="text-center">
                    <p className="text-slate-400 text-sm mb-1">Scope 2 (Indirect)</p>
                    <p className="text-3xl font-bold text-white">{formatNumber(summary.ghg?.scope2_tco2e, 1)}</p>
                    <p className="text-slate-500 text-xs">tCO₂e</p>
                    <div className="mt-2 bg-slate-700 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full"
                        style={{ width: `${summary.ghg?.scope2_percentage || 0}%` }}
                      />
                    </div>
                    <p className="text-slate-400 text-xs mt-1">{formatNumber(summary.ghg?.scope2_percentage, 1)}%</p>
                  </div>
                  <div className="text-center bg-slate-700 rounded-lg p-4">
                    <p className="text-slate-400 text-sm mb-1">Total GHG</p>
                    <p className="text-4xl font-bold text-green-400">{formatNumber(summary.ghg?.total_tco2e, 1)}</p>
                    <p className="text-slate-500 text-xs">tCO₂e</p>
                  </div>
                </div>
                
                {/* Grid Factor Info */}
                <div className="bg-slate-700/50 rounded-lg p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Info className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-300">
                      Grid Emission Factor: <span className="font-semibold text-white">{summary.ghg?.grid_ef_used} kg CO₂/kWh</span>
                    </span>
                  </div>
                  <span className="text-xs text-slate-400 bg-slate-600 px-2 py-1 rounded">
                    {summary.region} Grid
                  </span>
                </div>
                
                {/* Scope 1 Fuel Breakdown */}
                {summary.ghg?.scope1_breakdown && Object.keys(summary.ghg.scope1_breakdown).length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <p className="text-sm text-slate-400 mb-3">Scope 1 Breakdown by Fuel</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {Object.entries(summary.ghg.scope1_breakdown).map(([fuel, data]) => (
                        <div key={fuel} className="bg-slate-700 rounded-lg p-3">
                          <p className="text-white font-medium">{fuel}</p>
                          <p className="text-green-400 font-semibold">{formatNumber(data.total_tco2e, 2)} tCO₂e</p>
                          <p className="text-slate-500 text-xs">{formatNumber(data.energy_tj, 4)} TJ</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Intensities Section */}
          {summary.intensities?.available && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <button 
                onClick={() => toggleSection('intensities')}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100"
              >
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-indigo-600" />
                  <span className="font-semibold text-gray-900">Energy & Resource Intensities</span>
                  <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                    Per MT Output
                  </span>
                </div>
                {expandedSections.intensities ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
              </button>
              {expandedSections.intensities && (
                <div className="p-4">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-yellow-50 rounded-lg p-4 text-center">
                      <Zap className="w-5 h-5 text-yellow-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-gray-900">
                        {formatNumber(summary.intensities.energy_intensity_kwh_per_mt, 1)}
                      </p>
                      <p className="text-xs text-gray-500">kWh/MT</p>
                      <p className="text-xs text-gray-400 mt-1">Electricity</p>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4 text-center">
                      <Flame className="w-5 h-5 text-orange-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-gray-900">
                        {formatNumber(summary.intensities.fuel_intensity_gj_per_mt, 3)}
                      </p>
                      <p className="text-xs text-gray-500">GJ/MT</p>
                      <p className="text-xs text-gray-400 mt-1">Fuel</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <Droplets className="w-5 h-5 text-blue-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-gray-900">
                        {formatNumber(summary.intensities.water_intensity_kl_per_mt, 3)}
                      </p>
                      <p className="text-xs text-gray-500">KL/MT</p>
                      <p className="text-xs text-gray-400 mt-1">Water</p>
                    </div>
                    <div className="bg-gray-100 rounded-lg p-4 text-center">
                      <Recycle className="w-5 h-5 text-gray-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-gray-900">
                        {formatNumber(summary.intensities.waste_intensity_kg_per_mt, 2)}
                      </p>
                      <p className="text-xs text-gray-500">kg/MT</p>
                      <p className="text-xs text-gray-400 mt-1">Waste</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center border-2 border-green-300">
                      <PieChart className="w-5 h-5 text-green-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-green-700">
                        {formatNumber(summary.intensities.ghg_intensity_kgco2e_per_mt, 1)}
                      </p>
                      <p className="text-xs text-gray-500">kgCO₂e/MT</p>
                      <p className="text-xs text-gray-400 mt-1">GHG Intensity</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      Based on total production of {formatNumber(summary.intensities.production_mt)} MT
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Data Quality & Gaps Section */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <button 
              onClick={() => toggleSection('dataQuality')}
              className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100"
            >
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-blue-600" />
                <span className="font-semibold text-gray-900">Data Quality Assessment</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  summary.data_quality?.quality_level === 'high' ? 'bg-green-100 text-green-700' :
                  summary.data_quality?.quality_level === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {summary.data_quality?.quality_level?.toUpperCase()} ({formatNumber(summary.data_quality?.overall_score, 0)}%)
                </span>
              </div>
              {expandedSections.dataQuality ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </button>
            {expandedSections.dataQuality && (
              <div className="p-4 space-y-4">
                {/* Quality Breakdown */}
                <div className="grid grid-cols-4 gap-4">
                  {Object.entries(summary.data_quality?.breakdown || {}).map(([key, score]) => (
                    <div key={key} className="text-center">
                      <div className="relative w-16 h-16 mx-auto mb-2">
                        <svg className="w-16 h-16 transform -rotate-90">
                          <circle cx="32" cy="32" r="28" stroke="#e5e7eb" strokeWidth="4" fill="none" />
                          <circle 
                            cx="32" cy="32" r="28" 
                            stroke={score >= 80 ? '#22c55e' : score >= 50 ? '#eab308' : '#ef4444'}
                            strokeWidth="4" 
                            fill="none"
                            strokeDasharray={`${score * 1.76} 176`}
                          />
                        </svg>
                        <span className="absolute inset-0 flex items-center justify-center text-sm font-bold">
                          {formatNumber(score, 0)}%
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 capitalize">{key}</p>
                    </div>
                  ))}
                </div>

                {/* Data Gaps */}
                {summary.data_gaps?.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <FileWarning className="w-4 h-4 text-amber-600" />
                      <span className="font-medium text-amber-800">Data Gaps Identified</span>
                    </div>
                    <ul className="space-y-2">
                      {summary.data_gaps.map((gap, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <span className={`px-1.5 py-0.5 rounded text-xs ${
                            gap.severity === 'critical' ? 'bg-red-100 text-red-700' :
                            gap.severity === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {gap.severity}
                          </span>
                          <span className="text-gray-700">{gap.type}: {gap.issue}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Assumptions Made */}
                {summary.assumptions?.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <BookOpen className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-blue-800">Assumptions Applied</span>
                    </div>
                    <ul className="space-y-2">
                      {summary.assumptions.map((assumption, idx) => (
                        <li key={idx} className="text-sm text-gray-700">
                          <span className="font-medium">{assumption.type}:</span> {assumption.assumption}
                          <span className="text-gray-500 text-xs ml-2">({assumption.impact})</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Warnings */}
                {summary.warnings?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-4 h-4 text-red-600" />
                      <span className="font-medium text-red-800">Warnings</span>
                    </div>
                    <ul className="space-y-1">
                      {summary.warnings.map((warning, idx) => (
                        <li key={idx} className="text-sm text-red-700">• {warning}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Recommendation */}
                {summary.data_quality?.recommendation && (
                  <div className="bg-gray-100 rounded-lg p-3">
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Recommendation:</span> {summary.data_quality.recommendation}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Metadata Footer */}
          <div className="flex items-center justify-between text-xs text-gray-500 px-2">
            <span>
              Region: {summary.region} | Grid EF: {summary.ghg?.grid_ef_used} kgCO₂/kWh
            </span>
            <span>
              Calculated: {summary.calculated_at ? new Date(summary.calculated_at).toLocaleString() : 'N/A'}
            </span>
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 rounded-xl p-12 text-center">
          <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-700 mb-2">No Baseline Calculated</h4>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Click &quot;Calculate Baseline&quot; to generate comprehensive energy, emissions, and intensity metrics 
            based on your time-series data.
          </p>
          <button
            onClick={onCalculate}
            disabled={loading}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <BarChart3 className="w-5 h-5" />}
            Calculate Baseline
          </button>
        </div>
      )}
    </div>
  );
};

export default DesktopAssessment;
